/**
 * Typed API error carrying HTTP status and the server's `userError` flag.
 *
 * Errors with `userError: true` are expected, user-facing conditions (validation,
 * conflicts, not-found) that the server explicitly marked via the typed error catalog
 * (`packages/errors/src/user-errors.ts`). The global React Query error handlers
 * skip Sentry for these.
 */
export class ApiError extends Error {
  readonly status: number;
  /** True only when the server explicitly marked this as a registered user error. */
  readonly userError: boolean;
  /** The catalog code from USER_ERRORS, present only for registered user errors. */
  readonly code: string | undefined;
  /** Full JSON response body for callers that need typed extra fields. */
  readonly details: Record<string, unknown> | undefined;

  constructor(opts: {
    message: string;
    status: number;
    userError?: boolean;
    code?: string;
    details?: Record<string, unknown>;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.userError = opts.userError ?? false;
    this.code = opts.code;
    this.details = opts.details;
  }
}

export function isExpectedClientApiError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError && (error.userError || (error.status >= 400 && error.status < 500))
  );
}

/**
 * Parse a non-ok fetch response into an ApiError.
 *
 * Reads the JSON body to extract the error message and `userError` flag.
 * If the response isn't JSON or doesn't have the expected shape, falls back
 * to the provided message with `userError: false` (safe default — still reports to Sentry).
 */
export async function toApiError(
  response: Response,
  fallback = 'Request failed'
): Promise<ApiError> {
  let message = fallback;
  let userError = false;
  let code: string | undefined;
  let details: Record<string, unknown> | undefined;
  try {
    const data = await response.clone().json();
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      details = data as Record<string, unknown>;
      if (typeof details.error === 'string') message = details.error;
      if (details.userError === true) userError = true;
      if (typeof details.code === 'string') code = details.code;
    }
  } catch {
    // Response body isn't JSON — use fallback
  }
  return new ApiError({ message, status: response.status, userError, code, details });
}

export async function parseApiJson<T>(
  response: Response,
  fallback = 'Request failed'
): Promise<T> {
  if (!response.ok) throw await toApiError(response, fallback);
  return response.json() as Promise<T>;
}
